/**
 * Deterministic, tileable PBR fields for the Kraken's wet skin.
 *
 * One authored field feeds colour, relief and roughness so a pale scar is also raised
 * and comparatively dry, while a dark runnel catches a long wet highlight. Keeping the
 * three maps related is what stops procedural noise from reading as paint sprayed over
 * a smooth rubber tube.
 */

export interface OrganicSkinSample {
  /** Neutral colour multiplier applied over each anatomical vertex tint. */
  red: number;
  green: number;
  blue: number;
  /** Signed relief used to derive the tangent-space normal map. */
  height: number;
  /** Perceptual roughness before the material-level multiplier. */
  roughness: number;
  /** Exposed for deterministic tests and later close-detail geometry placement. */
  scar: number;
  /** Long glossy drainage path left by water sheeting off the animal. */
  runnel: number;
}

export interface OrganicSkinRaster {
  color: Uint8ClampedArray;
  normal: Uint8ClampedArray;
  metallicRoughness: Uint8ClampedArray;
}

const TAU = Math.PI * 2;

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

function wrap01(value: number): number {
  return value - Math.floor(value);
}

function narrowPeriodicLine(phase: number, sharpness: number): number {
  return Math.exp(-(Math.sin(phase) ** 2) * sharpness);
}

/**
 * Sample the continuous skin field.
 *
 * Every frequency crossing a tile boundary is an integer. The paths may wander inside
 * the tile, but opposite edges still meet exactly, which keeps hundred-metre limbs free
 * of a visible texture seam.
 */
export function organicSkinSample(rawU: number, rawV: number): OrganicSkinSample {
  const u = wrap01(rawU);
  const v = wrap01(rawV);

  const mantleFold = Math.sin(
    TAU * (u * 2 + v)
      + Math.sin(TAU * v * 3) * 0.92
      - Math.sin(TAU * u) * 0.28,
  );
  const crossedFold = Math.cos(
    TAU * (v * 3 - u)
      + Math.sin(TAU * u * 2) * 0.74,
  );
  const broadMottle = mantleFold * 0.62 + crossedFold * 0.38;

  const creaseGate = clamp01(
    0.18
      + Math.sin(TAU * (u * 2 - v * 3) + 0.7) * 0.78
      + Math.cos(TAU * (u * 5 + v)) * 0.34,
  );
  const crease = narrowPeriodicLine(
    TAU * (u * 3 + v * 2) + Math.sin(TAU * v * 2) * 1.18,
    62,
  ) * creaseGate;

  // Two sparse families make scars fork and end. A gate is important: an unbroken
  // periodic line would look like manufactured tread wrapped around every arm.
  const scarGate = Math.pow(
    clamp01(0.52 + Math.sin(TAU * (u - v * 2) + 1.1) * 0.72),
    3,
  );
  const scar = narrowPeriodicLine(
    TAU * (u + v * 2) + Math.sin(TAU * u * 3) * 0.56,
    150,
  ) * scarGate;

  const noduleSeed = Math.sin(TAU * (u * 11 + v * 7))
    * Math.sin(TAU * (u * 7 - v * 13));
  const nodules = Math.pow(Math.max(0, noduleSeed), 7);
  const pores = Math.abs(
    Math.sin(TAU * (u * 17 + v * 11))
      * Math.cos(TAU * (u * 13 - v * 19)),
  );

  const runnelGate = clamp01(
    0.24 + Math.sin(TAU * (u * 2 + v * 3) - 0.8) * 0.88,
  );
  const runnel = narrowPeriodicLine(
    TAU * (v * 4 + u) + Math.sin(TAU * u * 2) * 0.52,
    28,
  ) * runnelGate;

  const height = broadMottle * 0.18
    - crease * 0.17
    + scar * 0.28
    + nodules * 0.045;

  // Values stay close to one because the mesh tint owns the anatomical palette. Purple
  // bruising, pale scars and cold green-blue mottling remain visible without turning a
  // near-black animal into a camouflage print.
  const bruise = clamp01((-broadMottle - 0.12) * 0.72);
  const red = clamp01(0.87 + broadMottle * 0.055 + bruise * 0.065 + scar * 0.12);
  const green = clamp01(0.91 + broadMottle * 0.070 - bruise * 0.045 + scar * 0.055);
  const blue = clamp01(0.94 + broadMottle * 0.080 - bruise * 0.020 + scar * 0.035);
  const roughness = clamp01(
    0.70
      + pores * 0.15
      + crease * 0.10
      + scar * 0.16
      - runnel * 0.42,
  );

  return { red, green, blue, height, roughness, scar, runnel };
}

function byte(value: number): number {
  return Math.max(0, Math.min(255, Math.round(value * 255)));
}

/** Rasterise the linked PBR fields once at scene creation. */
export function buildOrganicSkinRaster(size: number): OrganicSkinRaster {
  if (!Number.isInteger(size) || size < 8) {
    throw new Error('Organic skin texture size must be an integer of at least 8');
  }

  const color = new Uint8ClampedArray(size * size * 4);
  const normal = new Uint8ClampedArray(size * size * 4);
  const metallicRoughness = new Uint8ClampedArray(size * size * 4);
  const texel = 1 / size;

  for (let y = 0; y < size; y++) {
    const v = y * texel;
    for (let x = 0; x < size; x++) {
      const u = x * texel;
      const sample = organicSkinSample(u, v);
      const dx = organicSkinSample(u + texel, v).height
        - organicSkinSample(u - texel, v).height;
      const dy = organicSkinSample(u, v + texel).height
        - organicSkinSample(u, v - texel).height;
      // The old map used the raw one-pixel difference and was almost perfectly flat.
      // This converts the height slope into a readable centimetre-scale fold while
      // retaining a smooth normal across broad wet areas.
      const nx = -dx * 7.2;
      const ny = -dy * 7.2;
      const invLength = 1 / Math.hypot(nx, ny, 1);
      const pixel = (y * size + x) * 4;

      color[pixel] = byte(sample.red);
      color[pixel + 1] = byte(sample.green);
      color[pixel + 2] = byte(sample.blue);
      color[pixel + 3] = 255;

      normal[pixel] = byte(nx * invLength * 0.5 + 0.5);
      normal[pixel + 1] = byte(ny * invLength * 0.5 + 0.5);
      normal[pixel + 2] = byte(invLength * 0.5 + 0.5);
      normal[pixel + 3] = 255;

      // Babylon reads green as roughness and blue as metallic. The animal remains a
      // dielectric; only the broken wetness changes.
      metallicRoughness[pixel] = 255;
      metallicRoughness[pixel + 1] = byte(sample.roughness);
      metallicRoughness[pixel + 2] = 0;
      metallicRoughness[pixel + 3] = 255;
    }
  }

  return { color, normal, metallicRoughness };
}
